import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ChevronDown, Globe } from 'lucide-react';

import { useLanguage } from 'shared/context/LanguageContext';
import { cn } from 'shared/lib/utils';

interface LanguageSwitcherProps {
  variant?: 'header' | 'inline' | 'compact';
  menuAlign?: 'directional' | 'right';
  landingAccent?: boolean;
  /** Keeps the full dropdown while reducing the header trigger to an icon on phones. */
  compactOnMobile?: boolean;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  variant = 'header',
  menuAlign = 'directional',
  landingAccent = false,
  compactOnMobile = false,
}) => {
  const { language, setLanguage, availableLanguages } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handle = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    if (open) {
      document.addEventListener('click', handle);
      return () => document.removeEventListener('click', handle);
    }
  }, [open]);

  const current = availableLanguages.find((l) => l.code === language);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-label={current?.nativeLabel}
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className={cn(
          'flex items-center transition-colors',
          variant === 'header' &&
            'gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50',
          compactOnMobile &&
            variant === 'header' &&
            'h-10 w-10 justify-center px-0 sm:w-auto sm:justify-start sm:px-3',
          variant === 'inline' &&
            'gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50',
          variant === 'compact' &&
            'h-8 gap-1 bg-transparent px-1 text-xs font-medium text-gray-500 hover:text-[#bf0035]',
        )}
      >
        {variant !== 'compact' ? (
          <Globe
            size={16}
            className={landingAccent ? 'text-[#bf0035]' : 'text-haat-red-600'}
          />
        ) : null}
        <span
          className={cn(
            'font-medium',
            compactOnMobile && 'hidden sm:inline',
          )}
        >
          {current?.nativeLabel}
        </span>
        <motion.span
          animate={variant === 'compact' ? undefined : { rotate: open ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className={cn(compactOnMobile && 'hidden sm:inline-flex')}
        >
          <ChevronDown size={14} className="text-gray-400" />
        </motion.span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={{ duration: 0.15 }}
            className={cn(
              'absolute z-50 min-w-[160px] rounded-xl border border-gray-200 bg-white shadow-xl p-1.5',
              variant === 'compact' ? 'bottom-full mb-2' : 'mt-2',
              menuAlign === 'right' ? 'right-0' : 'ltr:right-0 rtl:left-0',
            )}
            onClick={(e) => e.stopPropagation()}
          >
            {availableLanguages.map((lang) => {
              const isActive = lang.code === language;
              return (
                <button
                  key={lang.code}
                  type="button"
                  onClick={() => {
                    setLanguage(lang.code);
                    setOpen(false);
                  }}
                  className={cn(
                    'flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
                    isActive
                      ? landingAccent
                        ? 'bg-[#bf0035]/10 font-semibold text-[#bf0035]'
                        : 'bg-haat-red-50 text-haat-red-700 font-semibold'
                      : 'text-gray-700 hover:bg-gray-100',
                  )}
                >
                  <span>{lang.nativeLabel}</span>
                  {isActive && (
                    <Check
                      size={14}
                      className={landingAccent ? 'text-[#bf0035]' : 'text-haat-red-600'}
                    />
                  )}
                </button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LanguageSwitcher;
